import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { allProperties } from '../../mocks/properties';
import PropertyGallery from './components/PropertyGallery';
import PropertyInfo from './components/PropertyInfo';
import PropertyContact from './components/PropertyContact';
import LanguageSelector, { LangCode, getTranslation } from '../../components/feature/LanguageSelector';

export default function PropertyDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [lang, setLang] = useState<LangCode>('es');
  const property = allProperties.find(p => p.slug === slug);

  const t = (key: string) => getTranslation(lang, key);

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-home-line text-red-500 text-4xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('prop_not_found_title')}</h2>
          <p className="text-gray-500 mb-6">{t('prop_not_found_sub')}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-red-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t('prop_not_found_btn')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Top Bar */}
      <div className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors cursor-pointer font-medium text-sm"
          >
            <i className="ri-arrow-left-line text-lg"></i>
            {t('prop_back')}
          </button>
          <img
            src="https://static.readdy.ai/image/9fcc4035fe7cce5c6ba56d5b353fae35/5557e1bc3a7b24f13710d01af9330290.jpeg"
            alt="Red Lions Realty"
            className="h-9 w-auto object-contain"
          />
          <div className="flex items-center gap-3">
            <LanguageSelector currentLang={lang} onChangeLang={setLang} scrolled={true} />
            <a
              href="https://wa.me/34744780446"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center gap-2 bg-green-500 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-green-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-base"></i>
              WhatsApp
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        {/* Title + Badge */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="bg-red-600 text-white px-4 py-1.5 rounded-full text-sm font-semibold">
              {property.type}
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">{property.title}</h1>
          <p className="text-gray-500 flex items-center gap-2 text-base sm:text-lg">
            <i className="ri-map-pin-line text-red-600"></i>
            {property.location}
          </p>
        </div>

        {/* Gallery */}
        <PropertyGallery property={property} />

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 sm:gap-12 mt-10 sm:mt-14">
          <div className="lg:col-span-2">
            <PropertyInfo property={property} lang={lang} />
          </div>
          <div className="lg:col-span-1">
            <PropertyContact property={property} lang={lang} />
          </div>
        </div>
      </div>

      {/* Footer simple */}
      <footer className="bg-gray-900 text-white mt-16 mx-3 sm:mx-8 mb-4 sm:mb-8 rounded-3xl">
        <div className="max-w-7xl mx-auto px-6 sm:px-16 py-8 sm:py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-400 text-sm">{t('prop_footer_rights')}</p>
          <div className="flex items-center gap-6">
            <a href="https://www.instagram.com/redlionsrealty" target="_blank" rel="nofollow noopener noreferrer" className="text-white hover:text-red-400 transition-colors text-sm">Instagram</a>
            <a href="https://www.tiktok.com/@red.lions.realty" target="_blank" rel="nofollow noopener noreferrer" className="text-white hover:text-red-400 transition-colors text-sm">TikTok</a>
          </div>
        </div>
      </footer>

      {/* WhatsApp flotante */}
      <a
        href="https://wa.me/34744780446"
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="fixed bottom-6 right-6 z-50 flex items-center justify-center rounded-full shadow-lg hover:scale-110 transition-all duration-300 cursor-pointer"
        style={{ width: '56px', height: '56px', backgroundColor: '#25D366' }}
        aria-label="Contactar por WhatsApp"
      >
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}
